import { NewMap } from "@synergy/maps";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { io } from "socket.io-client";
import styled from "styled-components";
import { useAuthContext } from "../../contexts/AuthContext";
import { useGlobalFilterContext } from "../../contexts/GlobalFilterProvider";
import logger from "../../server/server";
import { PageContent } from "../../styles/global/Pages.styled";
import Theme from "../../styles/Theme/Theme";
import { REACT_APP_MAPS_API_KEY, websocketPort } from "../../utils/config/configs";
import { getColorBySeverity } from "../../utils/hooks/getColorByPriority";
import { getLogItemTooltipFields, isLogItem } from "../../utils/hooks/logItems";
import Spinner from "../Global/Atomics/Spinner";
import { CreateIncidentsOverlay } from "../Global/Molecules/Overlays/CreateIncidentsOverlay";
import { WidgetsOverlay } from "../Global/Molecules/Overlays/WidgetOverlay";
import MapDrawer from "../Global/Organism/Maps/MapDrawer";

const socket = io(websocketPort);

export const Map = ({ headerheight }) => {
  const { t, i18n } = useTranslation();
  const [mapConfigurations, setMapConfigurations] = useState(null);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const { user, tenant } = useAuthContext();

  //Create Incidents Mode
  const [rigthClickCoordinates, setRigthClickCoordinates] = useState(null);
  const [createIncidentModal, setCreateIncidentModal] = useState(false);
  const [isOnCreateIncidentMode, setIsOnCreateIncidentMode] = useState(false);
  const [mapClickCoordinates, setMapClickCoordinates] = useState(null);

  const [selectedFloor, setSelectedFloor] = useState(null);

  const { entity, subEntity, isToApplyFilters,
    mapItemsIncidentsData, isFetchingMapIncidentsData,
    // mapItemsServicesData, isFetchingMapServicesData, 
    selectedLayer, setSelectedLayer, copyMapPois, setCopyMapPois, refetchMapItemsIncidents
  } = useGlobalFilterContext();

  useEffect(() => {
    if (mapItemsIncidentsData && (subEntity || entity)) {
      const configurationsCopy = {
        ...defaultMapConfigurations,
        lat: Number(subEntity?.latitude ?? entity?.latitude),//38.71956666763748
        lng: Number(subEntity?.longitude ?? entity?.longitude),//-9.137763909977604
        zoom: subEntity?.zoom === 0 ? null : subEntity?.zoom
      };
      setMapConfigurations(configurationsCopy);
    }
  }, [mapItemsIncidentsData, subEntity, entity]);

  useEffect(() => {
    if (selectedPOI) {
      showDrawer();
    }
  }, [selectedPOI]);

  useEffect(() => {
    if (rigthClickCoordinates) {
      setCreateIncidentModal(true);
    }
  }, [rigthClickCoordinates]);

  useEffect(() => {
    socket.on("manageIncident", (text) => {
      console.log('Refetch incidents');
      refetchMapItemsIncidents();
    });
  }, [])

  const handleMarkerClick = (markerId, event) => {
    try {
      const clickedMarker = copyMapPois?.find(
        (marker) => marker.id === markerId
      );
      setSelectedPOI(clickedMarker);
    } catch (error) {
      console.error("(MapComponent page) Error in handling Marker Click:", error);
      logger.error("(MapComponent page) User : " + user?.Username + " Tenant: " + tenant + ", Error in handling Marker Click: ", error);
    }
  };

  const showDrawer = () => {
    setOpenDrawer(true);
    logger.info('User: ' + user?.Username + ' Tenant: ' + tenant + ', Drawer opened!')
  };

  const onCloseDrawer = () => {
    setOpenDrawer(false);
    setSelectedPOI(null);
    logger.info('User: ' + user?.Username + ' Tenant: ' + tenant + ', Drawer closed!')
  };

  const changeLayer = (layer) => {
    setSelectedLayer(layer);
  }

  // const filterPOIs = (filtredProperties, type) => {
  //   var filteredPOIs;
  //   if (type === 'priority') {
  //     filteredPOIs = mapPois?.filter(poi => filtredProperties?.includes(poi.priority)) ?? [];
  //   } else {
  //     filteredPOIs = mapPois?.filter(poi => filtredProperties?.includes(poi.class)) ?? [];
  //   }
  //   setCopyMapPois(filteredPOIs);
  // };

  // const redirectToDigitalTwin = (value) => {
  //   if (value.id) {
  //     window.location.href = `/registry/item/${value.id}/overview/Digital Twin`;
  //   };
  // };

  /**
   * Serializes an array of POIs (Points of Interest) for the map.
   * Used just for layer 'Incidents'.
   *
   * @param {Array} pois - The array of POIs to be serialized.
   * @returns {Array} - The serialized array of POIs with additional properties.
   */
  const serializePoisForMap = (pois) => {

    return pois.map(item => ({
      ...item,
      icon: isLogItem(item) ? 'Information' : null,
      iconColor: isLogItem(item) ? Theme.color["log-item"] : getColorBySeverity(item.severity),
      bgPadding: '2px',
      tooltipFields: isLogItem(item) ? getLogItemTooltipFields(t, i18n.language) : null,
    }
    ));
  }

  /**
   * Normalizes the coordinates object by extracting the latitude and longitude values.
   * If the coordinates object has a 'poi' property, it will use the 'poi' property's latitude and longitude.
   * Otherwise, it will use the 'lat' and 'lng' properties directly.
   *
   * @param {Object} coordinates - The coordinates object to normalize.
   * @param {Object} coordinates.poi - The 'poi' property of the coordinates object.
   * @param {number} coordinates.poi.latitude - The latitude value of the 'poi' property.
   * @param {number} coordinates.poi.longitude - The longitude value of the 'poi' property.
   * @param {number} coordinates.lat - The latitude value.
   * @param {number} coordinates.lng - The longitude value.
   * @returns {Object} - The normalized coordinates object with 'lat' and 'lng' properties.
   */
  // const normalizeCoordinates = (coordinates) => {
  //   if (!coordinates) return null;

  //   if (coordinates.poi) {
  //     return { lat: coordinates.poi.latitude, lng: coordinates.poi.longitude };
  //   }
  //   return { lat: coordinates.lat, lng: coordinates.lng };
  // };

  const addNewIncidentToMap = (incident) => {
    if (!incident) return;
    setCopyMapPois((prevPOIs) => [...prevPOIs, incident]);
  };

  return (
    <PageContent style={{ height: "100%", overflow: 'hidden' }}>
      <MapWrapper $marginTop={headerheight}>
        {mapConfigurations && copyMapPois && !isFetchingMapIncidentsData ? (
          <>
            <SpinnerMap id="spinnerMap" className={isFetchingMapIncidentsData ? 'is-loading' : ''}><Spinner stroke="green-4" size='72px' /></SpinnerMap>

            <MapContainer>
              <NewMap
                apiKey={REACT_APP_MAPS_API_KEY}
                configurations={mapConfigurations}
                pois={selectedLayer === 'Incidents' ? serializePoisForMap(copyMapPois) : copyMapPois}
                onMarkerClick={(id, event) => handleMarkerClick(id, event)}
                onMapClickFunction={isOnCreateIncidentMode ? (coordenates) => { setMapClickCoordinates(coordenates) } : null}
                onClusterClick={(cluster) => { setSelectedCluster(cluster); showDrawer(); }}
                isSvgIcons={selectedLayer === 'Incidents'}
                tooltipFields={
                  selectedLayer === 'Incidents' ?
                    {
                      gridColumns: 2,
                      fields: [
                        {
                          fieldName: t("input.incident_number"),
                          keyName: "name"
                        },
                        {
                          fieldName: t("input.location"),
                          keyName: "serviceName"
                        },
                        {
                          fieldName: t("input.fa_owner_group"),
                          keyName: "issueOwnerGroup"
                        },
                        {
                          fieldName: t("input.lastUpdate"),
                          keyName: "lastUpdate",
                          type: "date",
                          locale: i18n.language,
                        }
                      ],
                    }
                    :
                    null
                }
              />
            </MapContainer>
          </>
        ) : (
          <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Spinner stroke="green-4" />
          </div>
        )}
      </MapWrapper>

      <MapDrawer
        onCloseDrawer={onCloseDrawer}
        openDrawer={openDrawer}
        selectedCluster={selectedCluster}
        selectedLayer={selectedLayer}
        selectedPOI={selectedPOI}
        setSelectedPOI={setSelectedPOI}
      />

      <Overlay padding={isOnCreateIncidentMode ? '0' : `${Theme.spacing["s07"]} ${Theme.spacing["s06"]}`}>
        {isOnCreateIncidentMode ? <CreateIncidentsOverlay close={() => { setMapClickCoordinates(null); setIsOnCreateIncidentMode(false) }}
          mapClickCoordinates={mapClickCoordinates} addNewIncidentToMap={addNewIncidentToMap} /> : null}

        {subEntity && !isOnCreateIncidentMode ? <WidgetsOverlay selectedPOI={selectedPOI} selectedLayer={selectedLayer} copyMapPois={copyMapPois}
          changeLayer={changeLayer} subEntity={subEntity} isToApplyFilters={isToApplyFilters}
          openClusterDrawer={() => { setSelectedCluster({ 'coordenates': copyMapPois }); showDrawer() }}
          setIsOnCreateIncidentMode={() => setIsOnCreateIncidentMode(true)} /> : null}
      </Overlay>

      {/* <MapCreateIncidentModal isCreateIncidentModalOpen={createIncidentModal} setCreateIncidentModal={setCreateIncidentModal}
        mapClickCoordinates={normalizeCoordinates(rigthClickCoordinates)}
        selectedPOI={rigthClickCoordinates?.poi}
      /> */}
    </PageContent>
  );
}

// const offStreeParkingCorrection = () => {
//   return 'Off Street Parking';
// }

//#region  Styled Components
const MapWrapper = styled.div`
  height: ${(props) => props.$marginTop ? "calc(100% - " + props.$marginTop + ")" : "100%"};
  margin-top: ${(props) => (props.$marginTop ? props.$marginTop : "0")};
  padding: 0;
  margin-left: ${(props) => (props.$marginLeft ? props.$marginLeft : "0")};
  width: -webkit-fill-available;
  position: absolute;
`;

const Wrapper3D = styled.div`
  height: ${(props) => props.$marginTop ? "calc(100% - " + props.$marginTop + ")" : "100%"};
  margin-top: ${(props) => (props.$marginTop ? props.$marginTop : "0")};
  padding: 0;
  margin-left: ${(props) => (props.$marginLeft ? props.$marginLeft : "0")};
  width: -webkit-fill-available;
  position: absolute;
  width: 1519px;
  height: 1090px;
  flex-shrink: 0;
  border-radius: 1090px;
  background: var(--Neutral-2, #E6EDF0);
  margin: -100px 0px 0px 700px;
`;

const DrawerContainer = styled.div`
  position: relative;
  // height: 100%;
  width: 100%;
`;

const SpinnerMap = styled.div`
  z-index: 20;
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  right: 0;
  place-self: center;
  display: none;

  &.is-loading {
    display: block;
  }
`;
const TransitionWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-origin: ${(props) => props.origin};
  opacity: ${(props) => (props.transitioning)};
  transition: opacity 2s ease-in-out;
`;

const MapContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
`;

// Styling for the overlay
const Overlay = styled.div`
  padding: ${(props) => props.padding ? props.padding : null};
`;
//#endregion

const defaultMapConfigurations = {
  "heading": 0,
  "hide": [
    "attraction",
    "sports_complex",
    "place_of_worship",
    "business",
    "government",
    "medical",
    "park",
    "school"
  ],
  "mapId": "15fb961c1d0a757",
  "showGTFSRealTime": false,
  "showPOIS": true,
  "showTraffic": false,
  "showTransit": false,
  "showPolygon": false,
  "showEvents": false,
  "styles": "default",
  "tilt": 0,
  "zoom": 12,
  "hrefLink": "/home/register/registerDetail/",
  "poiType": "pois-color-by-family",
  "showLegend": false,
  "legendArray": [],
  "legendDetail": true,
  "hideEventsInfo": false,
  "circleRadius": null
}